import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './POImportResults.css';
import PORowEditor from './PORowEditor';
import API_BASE_URL from '../config';

// Submitting messages - defined outside component to avoid useEffect dependency issues
const SUBMITTING_MESSAGES = [
  'กำลังเชื่อมต่อ AME API...',
  'กำลังตรวจสอบข้อมูล...',
  'กำลังส่งข้อมูลลูกค้า...',
  'กำลังอัพโหลดไฟล์เอกสาร...',
  'รอการตอบกลับจาก AME...',
  'กำลังบันทึกผลลัพธ์...'
];

function POImportResults({ results, onClose }) {
  const [editingRows, setEditingRows] = useState(
    results.processedRows.map(row => ({ ...row, editing: false }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Fixed to V2 API only
  const apiVersion = 'v2';

  // Environment status (UAT or Production)
  const [environment, setEnvironment] = useState('uat');

  // Callback URL from backend config
  const [callbackUrl, setCallbackUrl] = useState(null);

  // Fetch callback URL and environment on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Fetch callback URL
        const callbackResponse = await axios.get(`${API_BASE_URL}/api/config/callback-url`);
        if (callbackResponse.data.success && callbackResponse.data.return_status_url) {
          setCallbackUrl(callbackResponse.data.return_status_url);
          console.log('📡 Callback URL loaded:', callbackResponse.data.return_status_url);
        }

        // Fetch environment
        const envResponse = await axios.get(`${API_BASE_URL}/api/config/environment`);
        if (envResponse.data.success) {
          setEnvironment(envResponse.data.environment);
          console.log('🌍 Environment:', envResponse.data.environment);
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch config:', error.message);
      }
    };
    fetchConfig();
  }, []);

  // Typing animation states
  const [typingText, setTypingText] = useState('');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const typingIntervalRef = useRef(null);

  // Typing animation effect
  useEffect(() => {
    if (!submitting) {
      setTypingText('');
      setCurrentMessageIndex(0);
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
      return;
    }

    const currentMessage = SUBMITTING_MESSAGES[currentMessageIndex];
    let charIndex = 0;
    setTypingText('');

    typingIntervalRef.current = setInterval(() => {
      if (charIndex <= currentMessage.length) {
        setTypingText(currentMessage.slice(0, charIndex));
        charIndex++;
      } else {
        // Finished typing current message, wait then move to next
        clearInterval(typingIntervalRef.current);
        setTimeout(() => {
          if (submitting) {
            setCurrentMessageIndex(prev =>
              (prev + 1) % SUBMITTING_MESSAGES.length
            );
          }
        }, 1500); // Wait 1.5s before next message
      }
    }, 50); // Type each character every 50ms

    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, [submitting, currentMessageIndex]);

  const handleRowUpdate = (index, updatedData) => {
    const newRows = [...editingRows];
    newRows[index] = {
      ...newRows[index],
      mappedData: {
        ...newRows[index].mappedData,
        ...updatedData
      }
    };
    setEditingRows(newRows);
  };

  // Save row and mark as ready
  const handleSaveRow = (index, data) => {
    const newRows = [...editingRows];
    newRows[index] = {
      ...newRows[index],
      status: 'ready', // Mark as ready after saving
      unmappedFields: [], // Clear unmapped fields
      editing: false // Close editor
    };
    setEditingRows(newRows);
    console.log('✅ Row saved and marked as ready:', newRows[index]);
  };

  const handleToggleEdit = (index) => {
    const newRows = [...editingRows];
    newRows[index].editing = !newRows[index].editing;
    setEditingRows(newRows);
  };

  // Helper function to convert Buddhist Era (พ.ศ.) to Christian Era (ค.ศ.)
  const convertBEtoAD = (dateStr) => {
    if (!dateStr) return dateStr;

    // Check if date is in format YYYY-MM-DD
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return dateStr;

    const year = parseInt(match[1]);
    const month = match[2];
    const day = match[3];

    // If year > 2500, assume it's Buddhist Era (พ.ศ.)
    if (year > 2500) {
      const adYear = year - 543;
      return `${adYear}-${month}-${day}`;
    }

    return dateStr;
  };

  // Copy JSON to clipboard
  const handleCopyJSON = () => {
    const jsonString = JSON.stringify(previewData, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      alert('✅ Copied JSON to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('❌ Failed to copy JSON');
    });
  };

  // Helper function to convert date format for new API (yyyy-mm-dd to yyyy/mm/dd)
  const convertDateToSlash = (dateStr) => {
    if (!dateStr) return dateStr;
    return dateStr.replace(/-/g, '/');
  };

  // Show preview before submit
  const handlePreviewSubmit = () => {
    const readyRows = editingRows.filter(r => r.status === 'ready');

    if (readyRows.length === 0) {
      alert('⚠️ ไม่มีข้อมูลที่พร้อมส่ง กรุณาบันทึกข้อมูลก่อน');
      return;
    }

    // Prepare data for preview based on API version
    let submitData;

    if (apiVersion === 'v2') {
      // New API format: /Car_insurance/edit
      submitData = readyRows.map(row => ({
        rowIndex: row.rowIndex,
        data: {
          // Customer information
          title: row.mappedData.title,
          first_name: row.mappedData.first_name,
          last_name: row.mappedData.last_name,
          citizen_id: row.mappedData.citizen_id,
          gender: row.mappedData.gender,
          birth_date: convertBEtoAD(row.mappedData.birth_date),
          mobile_number: row.mappedData.mobile_number,
          phone_number: row.mappedData.phone_number || '',
          line_id: row.mappedData.line_id || '',

          // Address with names
          address: row.mappedData.address,
          address_province_id: row.mappedData.address_province_id,
          address_province: row.mappedData.address_province || '',
          address_district_id: row.mappedData.address_district_id,
          address_district: row.mappedData.address_district || '',
          address_sub_district_id: row.mappedData.address_sub_district_id,
          address_sub_district: row.mappedData.address_sub_district || '',
          postcode: row.mappedData.postcode,

          // Car information with names
          car_brand_id: row.mappedData.car_brand_id,
          car_brand_name: row.mappedData.car_brand_name || '',
          car_model_id: row.mappedData.car_model_id,
          car_model_name: row.mappedData.car_model_name || '',
          car_category_id: row.mappedData.car_category_id || 1,
          reg_year: row.mappedData.reg_year,
          chassis_number: row.mappedData.car_number, // New field name
          plate_number: row.mappedData.reg_number, // New field name

          // Insurance information
          purchase_date: convertBEtoAD(row.mappedData.purchase_date),
          noti_number: row.mappedData.noti_number,
          insurance_company_id: row.mappedData.insurance_company_id,
          insurance_car_type_id: row.mappedData.insurance_car_type_id,
          insurance_number: row.mappedData.insurance_number || '',
          effective_date: convertDateToSlash(convertBEtoAD(row.mappedData.effective_date)),
          expiry_date: convertDateToSlash(convertBEtoAD(row.mappedData.expiry_date)),
          product_id: row.mappedData.product_id,
          product_name: row.mappedData.product_name || '',

          // Product details (from products-by-premium lookup)
          product_details: row.mappedData.product_details || null,

          // Prices - New API fields
          premium_price: row.mappedData.net_price_before || row.mappedData.price_before_vat,
          premium_real_price: row.mappedData.price_before_vat,
          net_price: row.mappedData.net_price,
          with_act: row.mappedData.with_act || 0,
          act_amount: row.mappedData.act_amount || 0,

          // Agent information
          service_by: row.mappedData.service_by || 0,
          agent_full_name: row.mappedData.agent_full_name || '',
          agent_telephone: row.mappedData.agent_telephone || '',
          agent_email: row.mappedData.agent_email || '',
          first_payment_by: row.mappedData.first_payment_by || 0,

          // Optional fields
          dchanel: row.mappedData.dchanel || '',
          remark: row.mappedData.remark || '',

          // Insurance document image URL (ไฟล์ภาพใบแจ้งงาน/หน้ากรมธรรม์)
          insurance_number_url: row.mappedData.insurance_number_url || '',

          // Callback URL for status updates from AME
          return_status_url: row.mappedData.return_status_url || callbackUrl
        }
      }));
    } else {
      // Old API format: /Customercar_insurance/insert_broker
      submitData = readyRows.map(row => ({
        rowIndex: row.rowIndex,
        data: {
          // Customer information
          title: row.mappedData.title,
          first_name: row.mappedData.first_name,
          last_name: row.mappedData.last_name,
          citizen_id: row.mappedData.citizen_id,
          gender: row.mappedData.gender,
          birth_date: convertBEtoAD(row.mappedData.birth_date),
          mobile_number: row.mappedData.mobile_number,
          phone_number: row.mappedData.phone_number || '',

          // Address
          address: row.mappedData.address,
          address_province_id: row.mappedData.address_province_id,
          address_district_id: row.mappedData.address_district_id,
          address_sub_district_id: row.mappedData.address_sub_district_id,
          postcode: row.mappedData.postcode,

          // Car information
          car_brand_id: row.mappedData.car_brand_id,
          car_model_id: row.mappedData.car_model_id,
          car_category_id: row.mappedData.car_category_id,
          reg_year: row.mappedData.reg_year,
          car_number: row.mappedData.car_number,
          reg_number: row.mappedData.reg_number,
          engine_number: row.mappedData.engine_number || '',

          // Insurance information
          purchase_date: convertBEtoAD(row.mappedData.purchase_date),
          noti_number: row.mappedData.noti_number,
          insurance_company_id: row.mappedData.insurance_company_id,
          insurance_car_type_id: row.mappedData.insurance_car_type_id,
          insurance_number: row.mappedData.insurance_number || '',
          effective_date: convertBEtoAD(row.mappedData.effective_date),
          expiry_date: convertBEtoAD(row.mappedData.expiry_date),
          product_id: row.mappedData.product_id,

          // Prices
          price_before_vat: row.mappedData.price_before_vat,
          net_price_before: row.mappedData.net_price_before,
          net_price: row.mappedData.net_price,

          // Agent information
          agent_full_name: row.mappedData.agent_full_name || 'Assign To',
          agent_telephone: '021148777',
          agent_email: 'aec@clickbroker.co.th',
          with_act: 0,
          service_by: 0,
          first_payment_by: 0,

          // Callback URL for status updates from AME
          return_status_url: row.mappedData.return_status_url || callbackUrl
        }
      }));
    }

    setPreviewData(submitData);
    setShowPreview(true);
  };

  const handleSubmitAll = async () => {
    setSubmitting(true);
    setShowPreview(false); // Close preview

    try {
      // Filter only ready rows
      const readyRows = editingRows.filter(r => r.status === 'ready');

      if (readyRows.length === 0) {
        alert('⚠️ ไม่มีข้อมูลที่พร้อมส่ง กรุณาบันทึกข้อมูลก่อน');
        setSubmitting(false);
        return;
      }

      console.log(`📤 Submitting ${readyRows.length} rows to AME API (${apiVersion})...`);

      const response = await axios.post(`${API_BASE_URL}/api/po/submit`, {
        rows: readyRows,
        apiVersion: apiVersion
      });

      if (response.data.success) {
        const { successCount, failCount, results } = response.data;

        console.log(`✅ Submission complete: ${successCount} succeeded, ${failCount} failed`);

        // Show detailed results
        let message = `✅ ส่งข้อมูลสำเร็จ ${successCount}/${readyRows.length} รายการ`;

        if (failCount > 0) {
          const failedRows = results.filter(r => !r.success).map(r => r.rowIndex).join(', ');
          message += `\n\n❌ ส่งไม่สำเร็จ ${failCount} รายการ (แถว: ${failedRows})`;

          // Show first error detail
          const firstError = results.find(r => !r.success);
          if (firstError) {
            message += `\n\nข้อผิดพลาด: ${firstError.error}`;
          }
        }

        alert(message);

        // If all successful, close the import results
        if (failCount === 0) {
          setTimeout(() => {
            if (window.confirm('✅ ส่งข้อมูลสำเร็จทั้งหมด ต้องการปิดหน้าต่างนี้หรือไม่?')) {
              onClose();
            }
          }, 500);
        }
      } else {
        alert(`❌ เกิดข้อผิดพลาด: ${response.data.error}`);
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      alert(`❌ เกิดข้อผิดพลาดในการส่งข้อมูล: ${error.response?.data?.error || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const readyCount = editingRows.filter(r => r.status === 'ready').length;
  const needsConfirmationCount = editingRows.filter(r => r.status === 'needs_confirmation').length;

  return (
    <div className="import-results-container">
      <div className="import-results-card">
        {/* Combined Header Row - Title, Stats, API Endpoint, Environment */}
        <div className="results-header">
          <div className="header-left">
            <h2>📋 Import Results</h2>
            <div className="header-stats">
              <span className="stat-item">
                <span className="stat-label">Total:</span>
                <span className="stat-value">{results.totalRows}</span>
              </span>
              <span className="stat-item ready">
                <span className="stat-label">✅ Ready:</span>
                <span className="stat-value">{readyCount}</span>
              </span>
              <span className="stat-item needs-confirm">
                <span className="stat-label">⚠️ Needs:</span>
                <span className="stat-value">{needsConfirmationCount}</span>
              </span>
            </div>
          </div>
          <div className="header-right">
            <div className="api-endpoint-badge">
              V2: /Car_insurance/edit
            </div>
            <div className={`environment-badge ${environment}`}>
              {environment === 'production' ? '🔴 Production' : '🟡 UAT'}
            </div>
            <button onClick={onClose} className="btn-close">✕</button>
          </div>
        </div>

        {/* Info Message */}
        {needsConfirmationCount > 0 && (
          <div className="info-message">
            ℹ️ Some fields couldn't be automatically mapped. Please review and confirm the values below.
          </div>
        )}

        {/* Rows List */}
        <div className="rows-list">
          {editingRows.map((row, index) => (
            <div key={index} className={`row-card ${row.status}`}>
              <div className="row-header">
                <div className="row-title">
                  <span className="row-index">#{row.rowIndex}</span>
                  <span className="row-po-number">
                    PO: {row.mappedData.po_number || '-'}
                  </span>
                  <span className="row-customer">
                    {row.mappedData.customer_name || '-'}
                  </span>
                </div>
                <div className="row-actions">
                  {row.status === 'needs_confirmation' && (
                    <span className="status-badge needs-confirm">
                      ⚠️ Needs Confirmation ({row.unmappedFields.length} fields)
                    </span>
                  )}
                  {row.status === 'ready' && (
                    <span className="status-badge ready">✅ Ready</span>
                  )}
                  <button
                    onClick={() => handleToggleEdit(index)}
                    className="btn-edit"
                  >
                    {row.editing ? '📖 View' : '✏️ Edit'}
                  </button>
                </div>
              </div>

              {/* Row Editor */}
              {row.editing && (
                <PORowEditor
                  row={row}
                  onUpdate={(updatedData) => handleRowUpdate(index, updatedData)}
                  onSave={(data) => handleSaveRow(index, data)}
                />
              )}

              {/* Quick View */}
              {!row.editing && (
                <div className="row-quick-view">
                  <div className="field-group">
                    <span className="field-label">วันที่:</span>
                    <span className="field-value">{row.mappedData.purchase_date || '-'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">รถยนต์:</span>
                    <span className="field-value">
                      {row.mappedData.car_brand_name || '-'} {row.mappedData.car_model_name || '-'}
                    </span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">ทะเบียน:</span>
                    <span className="field-value">
                      {row.mappedData.reg_number || row.originalData?.Car_Plate_No || '-'}
                    </span>
                  </div>
                  <div className="field-group field-group-full">
                    <span className="field-label">บริษัทประกัน:</span>
                    <span className="field-value">{row.mappedData.insurance_company_name || row.originalData?.Insurance_Company || '-'}</span>
                  </div>

                  {/* Show unmapped fields */}
                  {row.unmappedFields.length > 0 && (
                    <div className="unmapped-fields">
                      <strong>Missing fields:</strong>
                      <ul>
                        {row.unmappedFields.slice(0, 5).map((field, i) => (
                          <li key={i}>{field.label}</li>
                        ))}
                        {row.unmappedFields.length > 5 && (
                          <li>... and {row.unmappedFields.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="results-actions">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handlePreviewSubmit}
            disabled={submitting || needsConfirmationCount > 0}
            className="btn-primary"
          >
            {submitting ? '⏳ Submitting...' : `📋 Preview & Submit (${readyCount} rows)`}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <h2>📋 Preview Data to Submit</h2>
              <span className="api-version-badge">
                {apiVersion === 'v2' ? '🆕 V2: Car_insurance/edit' : 'V1: insert_broker'}
              </span>
              <button onClick={() => setShowPreview(false)} className="btn-close">✕</button>
            </div>

            <div className="preview-content">
              <div className="preview-summary">
                <p><strong>Total Rows:</strong> {previewData.length}</p>
                <p><strong>API Endpoint:</strong> {apiVersion === 'v2' ? '/Car_insurance/edit' : '/Customercar_insurance/insert_broker'}</p>
              </div>

              {/* Preview each row */}
              {previewData.map((item, idx) => (
                <div key={idx} className="preview-row">
                  <h3>Row #{item.rowIndex}</h3>

                  <div className="preview-section">
                    <h4>👤 Customer Information</h4>
                    <table className="preview-table">
                      <tbody>
                        <tr><td>Title:</td><td>{item.data.title}</td></tr>
                        <tr><td>First Name:</td><td>{item.data.first_name}</td></tr>
                        <tr><td>Last Name:</td><td>{item.data.last_name}</td></tr>
                        <tr><td>Citizen ID:</td><td>{item.data.citizen_id}</td></tr>
                        <tr><td>Gender:</td><td>{item.data.gender === 0 ? 'Male' : 'Female'}</td></tr>
                        <tr><td>Birth Date:</td><td>{item.data.birth_date}</td></tr>
                        <tr><td>Mobile:</td><td>{item.data.mobile_number}</td></tr>
                        {apiVersion === 'v2' && <tr><td>Line ID:</td><td>{item.data.line_id || '-'}</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  <div className="preview-section">
                    <h4>📍 Address</h4>
                    <table className="preview-table">
                      <tbody>
                        <tr><td>Address:</td><td>{item.data.address}</td></tr>
                        <tr><td>Province ID:</td><td>{item.data.address_province_id}</td></tr>
                        {apiVersion === 'v2' && <tr><td>Province:</td><td>{item.data.address_province || '-'}</td></tr>}
                        <tr><td>District ID:</td><td>{item.data.address_district_id}</td></tr>
                        {apiVersion === 'v2' && <tr><td>District:</td><td>{item.data.address_district || '-'}</td></tr>}
                        <tr><td>Sub-district ID:</td><td>{item.data.address_sub_district_id}</td></tr>
                        {apiVersion === 'v2' && <tr><td>Sub-district:</td><td>{item.data.address_sub_district || '-'}</td></tr>}
                        <tr><td>Postcode:</td><td>{item.data.postcode}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="preview-section">
                    <h4>🚗 Car Information</h4>
                    <table className="preview-table">
                      <tbody>
                        <tr><td>Brand ID:</td><td>{item.data.car_brand_id}</td></tr>
                        {apiVersion === 'v2' && <tr><td>Brand Name:</td><td>{item.data.car_brand_name || '-'}</td></tr>}
                        <tr><td>Model ID:</td><td>{item.data.car_model_id}</td></tr>
                        {apiVersion === 'v2' && <tr><td>Model Name:</td><td>{item.data.car_model_name || '-'}</td></tr>}
                        <tr><td>Category ID:</td><td>{item.data.car_category_id}</td></tr>
                        <tr><td>Reg Year:</td><td>{item.data.reg_year}</td></tr>
                        {apiVersion === 'v2' ? (
                          <>
                            <tr><td>Chassis Number:</td><td>{item.data.chassis_number}</td></tr>
                            <tr><td>Plate Number:</td><td>{item.data.plate_number}</td></tr>
                          </>
                        ) : (
                          <>
                            <tr><td>Car Number (Chassis):</td><td>{item.data.car_number}</td></tr>
                            <tr><td>Reg Number (Plate):</td><td>{item.data.reg_number}</td></tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="preview-section">
                    <h4>🏢 Insurance</h4>
                    <table className="preview-table">
                      <tbody>
                        <tr><td>Purchase Date:</td><td>{item.data.purchase_date}</td></tr>
                        <tr><td>Noti Number:</td><td>{item.data.noti_number}</td></tr>
                        <tr><td>Company ID:</td><td>{item.data.insurance_company_id}</td></tr>
                        <tr><td>Car Type ID:</td><td>{item.data.insurance_car_type_id}</td></tr>
                        <tr><td>Product ID:</td><td>{item.data.product_id}</td></tr>
                        {apiVersion === 'v2' && item.data.product_name && (
                          <tr><td>Product Name:</td><td>{item.data.product_name}</td></tr>
                        )}
                        <tr><td>Effective Date:</td><td>{item.data.effective_date}</td></tr>
                        <tr><td>Expiry Date:</td><td>{item.data.expiry_date}</td></tr>
                        {apiVersion === 'v2' ? (
                          <>
                            <tr><td>Premium Price (รวม VAT):</td><td>{item.data.premium_price}</td></tr>
                            <tr><td>Premium Real Price:</td><td>{item.data.premium_real_price}</td></tr>
                            <tr><td>Net Price:</td><td>{item.data.net_price}</td></tr>
                            <tr><td>With Act:</td><td>{item.data.with_act}</td></tr>
                            <tr><td>Act Amount:</td><td>{item.data.act_amount}</td></tr>
                          </>
                        ) : (
                          <>
                            <tr><td>Net Price:</td><td>{item.data.net_price}</td></tr>
                            <tr><td>Price Before VAT:</td><td>{item.data.price_before_vat}</td></tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Product Details Section (V2 only) */}
                  {apiVersion === 'v2' && item.data.product_details && (
                    <div className="preview-section">
                      <h4>📦 Product Details (สินค้าที่เลือก)</h4>
                      <table className="preview-table">
                        <tbody>
                          <tr><td>ชื่อสินค้า (Product Name):</td><td><strong>{item.data.product_name || '-'}</strong></td></tr>
                          <tr><td>ประเภทการชำระ (Payment Type):</td><td>{item.data.product_details.payment_type || '-'}</td></tr>
                          <tr><td>เบี้ยที่ใช้ค้นหา (Premium Lookup):</td><td>{item.data.product_details.premium_used_for_lookup?.toLocaleString() || '-'} บาท</td></tr>
                          <tr><td>งวดแรก (First Payment):</td><td>{item.data.product_details.first_payment?.toLocaleString() || '-'} บาท</td></tr>
                          <tr><td>จำนวนงวด (Installments):</td><td>{item.data.product_details.number_installment || '-'} งวด</td></tr>
                          <tr><td>จำนวนงวดจริง (Real Installments):</td><td>{item.data.product_details.real_number_installment || '-'} งวด</td></tr>
                          <tr><td>เปอร์เซ็นต์ดาวน์ (Down Payment %):</td><td>{item.data.product_details.down_payment_percent || '-'}%</td></tr>
                          <tr><td>ยอดผ่อน/งวด (Payment/Installment):</td><td>{item.data.product_details.payment_installment?.toLocaleString() || '-'} บาท</td></tr>
                          <tr><td>เปอร์เซ็นต์ค่าบริการ (Service %):</td><td>{item.data.product_details.service_percent || '-'}%</td></tr>
                          <tr><td>ค่าบริการ (Service Price):</td><td>{item.data.product_details.service_price?.toLocaleString() || '-'} บาท</td></tr>
                          <tr><td>ค่าบริการอื่นๆ (Other Service):</td><td>{item.data.product_details.service_other?.toLocaleString() || '-'} บาท</td></tr>
                          <tr><td>ยอดรวมงวดแรก (Total First Payment):</td><td><strong style={{color: '#1565c0'}}>{item.data.product_details.total_first_payment_amount?.toLocaleString() || '-'} บาท</strong></td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="preview-section">
                    <h4>👤 Agent Information</h4>
                    <table className="preview-table">
                      <tbody>
                        <tr><td>agent_full_name:</td><td>{item.data.agent_full_name}</td></tr>
                        <tr><td>agent_telephone:</td><td>{item.data.agent_telephone}</td></tr>
                        <tr><td>agent_email:</td><td>{item.data.agent_email}</td></tr>
                        <tr><td>with_act:</td><td>{item.data.with_act}</td></tr>
                        <tr><td>service_by:</td><td>{item.data.service_by}</td></tr>
                        <tr><td>first_payment_by:</td><td>{item.data.first_payment_by}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  {apiVersion === 'v2' && (
                    <div className="preview-section">
                      <h4>📝 Optional Fields (V2)</h4>
                      <table className="preview-table">
                        <tbody>
                          <tr><td>D-Channel:</td><td>{item.data.dchanel || '-'}</td></tr>
                          <tr><td>Remark:</td><td>{item.data.remark || '-'}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="preview-section">
                    <h4>📡 Callback Configuration</h4>
                    <table className="preview-table">
                      <tbody>
                        <tr>
                          <td>return_status_url:</td>
                          <td>
                            {item.data.return_status_url ? (
                              <span>{item.data.return_status_url}</span>
                            ) : (
                              <span className="auto-configured">
                                🔄 Auto-configured by backend (CALLBACK_BASE_URL/api/insurance/broker-response)
                              </span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                </div>
              ))}
            </div>

            <div className="preview-actions">
              <button onClick={handleCopyJSON} className="btn-secondary">
                📋 Copy JSON
              </button>
              <button onClick={() => setShowPreview(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleSubmitAll} className="btn-primary">
                ✅ Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submitting Overlay with Typing Animation */}
      {submitting && (
        <div className="submitting-overlay">
          <div className="submitting-modal">
            <div className="notebook-container">
              <div className="notebook-header">
                <div className="notebook-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <span className="notebook-title">AME API Terminal</span>
              </div>
              <div className="notebook-content">
                <div className="terminal-line">
                  <span className="prompt">$</span>
                  <span className="typing-text">{typingText}</span>
                  <span className="cursor">|</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
              </div>
            </div>
            <p className="submitting-hint">กรุณารอสักครู่ ระบบกำลังดำเนินการ...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default POImportResults;
